import { useRef, useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type ImportacionPresupuesto, type Presupuesto, type RubroPresupuesto } from "@/lib/api";
import { parseTipoCambio, tipoCambioAInput } from "@/lib/gastos";
import { aplicarOrden, lineasImportacion } from "@/lib/presupuesto";
import { useEdicionRubro } from "./useEdicionRubro";

type Importacion = { archivo: File; vista: ImportacionPresupuesto } | null;
/** El desglose de un rubro abierto: se pide la ficha la primera vez que se abre. */
export type Desglose = { cargando: true } | { cargando: false; ficha: RubroPresupuesto } | { cargando: false; error: string };

/**
 * Lógica de Presupuesto (sin JSX): comparación por rubro contra lo facturado y lo pagado (todo en USD, lo arma el
 * backend; el total de cada rubro es la suma de sus ítems, que se cargan en la ficha del rubro) y la importación de
 * la planilla de cómputo: elegir el Excel trae la vista previa, y "Importar" la confirma. Sólo el admin importa.
 * Los precios de los ítems van en pesos y pasan a USD con el tipo de cambio único del presupuesto, que el admin
 * cambia acá (el USD se recalcula; lo cargado antes en USD se pasa a pesos la primera vez).
 * Tocar un rubro lo despliega con sus ítems (Mati, 2026-10-28: "no quiero que se abra otra pestaña"), y ahí mismo
 * el admin agrega, edita y saca ítems y cambia sus etiquetas (`useEdicionRubro`); la ficha del rubro queda para
 * comparar ítem por ítem los presupuestos recibidos.
 * Rubros e ítems van numerados por su lugar (1, 1.1…) y el admin los reordena ("Ordenar rubros" / "Ordenar ítems",
 * `ListaOrdenable`): cada movida se ve al toque y se guarda en cola (en orden, aunque se muevan varios seguidos).
 */
export function usePresupuestoTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/presupuesto`;
  const esAdmin = membresia.rol === "admin";

  const recurso = useApiResource<Presupuesto>(base);
  const [importacion, setImportacion] = useState<Importacion>(null);
  const [resultado, setResultado] = useState<string[] | null>(null);
  const [desgloses, setDesgloses] = useState<Record<number, Desglose>>({});
  const [abiertos, setAbiertos] = useState<number[]>([]);
  // null = el campo muestra el tipo de cambio guardado; un texto, lo que el admin está escribiendo.
  const [tipoCambio, setTipoCambio] = useState<string | null>(null);
  const [errorTipoCambio, setErrorTipoCambio] = useState<string | null>(null);
  // Orden: el que el admin acaba de mover (se ve antes de que vuelva del servidor); null = el del servidor.
  const [ordenRubros, setOrdenRubros] = useState<number[] | null>(null);
  const [ordenando, setOrdenando] = useState<"rubros" | number | null>(null); // number = los ítems de ese rubro
  const [errorOrden, setErrorOrden] = useState<string | null>(null);
  const cola = useRef<Promise<unknown>>(Promise.resolve());
  const guardarOrden = (ruta: string, ids: number[], siFalla: () => void) => {
    setErrorOrden(null);
    cola.current = cola.current.then(() =>
      api.put<number[]>(ruta, { ids }).catch((e: unknown) => {
        setErrorOrden(e instanceof Error ? e.message : "No se pudo guardar el orden");
        siFalla();
      }),
    );
  };

  const pedirFicha = (rubroId: number) =>
    api.get<RubroPresupuesto>(`${base}/rubros/${rubroId}`).then(
      (ficha) => setDesgloses((d) => ({ ...d, [rubroId]: { cargando: false, ficha } })),
      (e: unknown) => setDesgloses((d) => ({ ...d, [rubroId]: { cargando: false, error: e instanceof Error ? e.message : "No se pudo cargar el rubro" } })),
    );

  const edicion = useEdicionRubro({
    tipoCambio: recurso.data?.tipo_cambio ?? null,
    alCambiar: (rubroId) => {
      void pedirFicha(rubroId);
      recurso.recargar(); // el total, la cantidad de ítems y las etiquetas del rubro
    },
  });

  const moverRubros = (ids: number[]) => {
    setOrdenRubros(ids);
    guardarOrden(`${base}/orden`, ids, () => {
      setOrdenRubros(null);
      recurso.recargar();
    });
  };

  const moverItems = (rubroId: number, ids: number[]) => {
    setDesgloses((d) => {
      const actual = d[rubroId];
      if (!actual || actual.cargando || "error" in actual) return d;
      const items = aplicarOrden(actual.ficha.items, ids, (i) => i.id).map((i, pos) => ({ ...i, orden: pos + 1 }));
      return { ...d, [rubroId]: { cargando: false, ficha: { ...actual.ficha, items } } };
    });
    guardarOrden(`${base}/rubros/${rubroId}/orden`, ids, () => void pedirFicha(rubroId));
  };

  const accionTipoCambio = useAsyncAction((tc: string) => api.put<{ tipo_cambio: string; convertidos: number }>(`${base}/tipo-cambio`, { tipo_cambio: tc }));
  const guardado = recurso.data?.tipo_cambio ?? null;
  const textoTipoCambio = tipoCambio ?? (guardado === null ? "" : tipoCambioAInput(guardado));

  const guardarTipoCambio = async () => {
    const tc = parseTipoCambio(textoTipoCambio);
    setErrorTipoCambio(tc === null ? "Ingresá el tipo de cambio (ARS por USD), por ejemplo 1450" : null);
    if (tc === null) return;
    if (await accionTipoCambio.run(tc)) {
      setTipoCambio(null);
      setDesgloses({}); // los USD de los ítems cambiaron
      setAbiertos([]);
      recurso.recargar();
    }
  };

  const alternar = (rubroId: number) => {
    if (abiertos.includes(rubroId)) {
      setAbiertos((a) => a.filter((x) => x !== rubroId));
      return;
    }
    setAbiertos((a) => [...a, rubroId]);
    const actual = desgloses[rubroId];
    if (actual && !("error" in actual)) return;
    setDesgloses((d) => ({ ...d, [rubroId]: { cargando: true } }));
    void pedirFicha(rubroId);
  };

  const subir = (archivo: File, confirmar: boolean) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    datos.append("confirmar", String(confirmar));
    return api.upload<ImportacionPresupuesto>(`${base}/importar`, datos);
  };
  const accionVista = useAsyncAction(subir);
  const accionImportar = useAsyncAction(subir);

  const elegirPlanilla = async (archivos: File[]) => {
    const [archivo] = archivos;
    if (!archivo) return;
    setResultado(null);
    accionImportar.limpiarError();
    const vista = await accionVista.run(archivo, false);
    if (vista) setImportacion({ archivo, vista });
  };

  const confirmarImportacion = async () => {
    if (!importacion) return;
    const hecho = await accionImportar.run(importacion.archivo, true);
    if (hecho) {
      setImportacion(null);
      setResultado(lineasImportacion(hecho));
      setDesgloses({});
      setAbiertos([]);
      recurso.recargar();
    }
  };

  return {
    esAdmin,
    presupuesto: recurso.data ?? null,
    /** Los rubros en el orden que se ven (el número de cada uno es su lugar). */
    rubros: recurso.data ? aplicarOrden(recurso.data.items, ordenRubros, (i) => i.rubro.id) : [],
    cargando: recurso.loading && !recurso.data,
    error: recurso.data ? null : (recurso.error?.message ?? null),
    elegirPlanilla,
    leyendoPlanilla: accionVista.loading,
    errorPlanilla: accionVista.error,
    importacion,
    lineasVista: importacion ? lineasImportacion(importacion.vista) : [],
    hayParaImportar: importacion ? importacion.vista.rubros_nuevos.length + importacion.vista.rubros_existentes.length > 0 : false,
    cerrarImportacion: () => setImportacion(null),
    confirmarImportacion,
    importando: accionImportar.loading,
    errorImportar: accionImportar.error,
    resultado,
    tipoCambio: textoTipoCambio,
    cambiarTipoCambio: setTipoCambio,
    /** Lo escrito difiere de lo guardado (con valor inválido también: así "Guardar" muestra el error). */
    tipoCambioCambiado: tipoCambio !== null && (guardado === null || Number(parseTipoCambio(tipoCambio)) !== Number(guardado)),
    guardarTipoCambio,
    guardandoTipoCambio: accionTipoCambio.loading,
    errorTipoCambio: errorTipoCambio ?? accionTipoCambio.error,
    /** null = el rubro está cerrado. */
    desglose: (rubroId: number): Desglose | null => (abiertos.includes(rubroId) ? (desgloses[rubroId] ?? { cargando: true }) : null),
    alternar,
    ordenando,
    /** Abre o cierra la lista para reordenar los rubros ("rubros") o los ítems de un rubro (su id). */
    alternarOrden: (que: "rubros" | number) => setOrdenando((o) => (o === que ? null : que)),
    moverRubros,
    moverItems,
    errorOrden,
    edicion,
  };
}
